import { Dialog } from "@constellation-academy/epic-ui";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { type DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import {
	AddCanAfterNDaysStartingTreeStartContentConfigInput,
	startContentConfigEditor_AddCanAfterNDaysStartContentConfigMutation,
} from "@relay/startContentConfigEditor_AddCanAfterNDaysStartContentConfigMutation.graphql";
import {
	type AddCanAfterStartingTreeStartContentConfigInput,
	type startContentConfigEditor_addCanAfterStartingTreeStartContentConfigAdminMutation,
} from "@relay/startContentConfigEditor_addCanAfterStartingTreeStartContentConfigAdminMutation.graphql";
import {
	AddCanIfCertificationUnlockedStartContentConfigInput,
	startContentConfigEditor_addCanIfCertificationUnlockedStartContentConfigAdminMutation,
} from "@relay/startContentConfigEditor_addCanIfCertificationUnlockedStartContentConfigAdminMutation.graphql";
import {
	type AddCanIfUnlockedStartContentConfigInput,
	type startContentConfigEditor_addCanIfUnlockedStartContentConfigAdminMutation,
} from "@relay/startContentConfigEditor_addCanIfUnlockedStartContentConfigAdminMutation.graphql";
import { type startContentConfigEditor_RemoveStartContentConfigMutation } from "@relay/startContentConfigEditor_RemoveStartContentConfigMutation.graphql";
import { type StartContentConfigType } from "@relay/startContentConfigEditor_TreeNodeFragment.graphql";
import {
	ADD_CAN_AFTER_STARTING_TREE_START_CONTENT_CONFIG_MUTATION,
	ADD_CAN_IF_CERTIFICATION_UNLOCKED_START_CONTENT_CONFIG_MUTATION,
	ADD_CAN_IF_UNLOCKED_START_CONTENT_CONFIG_MUTATION,
	ADD_CAN_SKIP_START_CONTENT_CONFIG_MUTATION,
	REMOVE_START_CONTENT_CONFIGURATION_MUTATION,
	TREE_NODE_FRAGMENT,
} from "./start-content-config-editor.graphql";
import {
	buttonClass,
	buttonRowClass,
	inputClass,
	inputWrapperClass,
	labelClass,
	startContentConfigDialogWrapperClass,
} from "./start-content-config-editor.styles";
import { type StartContentConfigEditorProps } from "./start-content-config-editor.types";
import { type Config, type ConfigurationGraphqlInterface } from "../Config.interfaces";
import { ConfigDropdown, type ConfigDropdownOptions } from "../ConfigDropdown";
import { ConfigItem } from "../ConfigItem";
import { ConfigPreview } from "../ConfigPreview";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const StartContentConfigEditor = ({
	treeNodeFragmentRef,
}: StartContentConfigEditorProps) => {
	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const treeNode = useFragment(TREE_NODE_FRAGMENT, treeNodeFragmentRef);

	const [isNDaysVisible, setIsNDaysVisible] = useState(false);
	const [numOfDaysInput, setNumOfDaysInput] = useState<number | null>(null);
	const [pendingNDaysInput, setPendingNDaysInput] =
		useState<AddCanAfterNDaysStartingTreeStartContentConfigInput | null>(null);

	const [removeStartContentConfig, isRemovingStartContentConfig] =
		useMutation<startContentConfigEditor_RemoveStartContentConfigMutation>(
			REMOVE_START_CONTENT_CONFIGURATION_MUTATION,
		);

	const [addCanIfCertificationUnlockedStartContentConfig] =
		useMutation<startContentConfigEditor_addCanIfCertificationUnlockedStartContentConfigAdminMutation>(
			ADD_CAN_IF_CERTIFICATION_UNLOCKED_START_CONTENT_CONFIG_MUTATION,
		);

	const [addCanAfterStartingTreeConfig] =
		useMutation<startContentConfigEditor_addCanAfterStartingTreeStartContentConfigAdminMutation>(
			ADD_CAN_AFTER_STARTING_TREE_START_CONTENT_CONFIG_MUTATION,
		);

	const [addCanAfterNDaysStartingTreeConfig] =
		useMutation<startContentConfigEditor_AddCanAfterNDaysStartContentConfigMutation>(
			ADD_CAN_SKIP_START_CONTENT_CONFIG_MUTATION,
		);

	const [addCanIfUnlockedStartContentConfig] =
		useMutation<startContentConfigEditor_addCanIfUnlockedStartContentConfigAdminMutation>(
			ADD_CAN_IF_UNLOCKED_START_CONTENT_CONFIG_MUTATION,
		);

	const configs: Array<Config<StartContentConfigType, any | never>> = [
		{
			// Is present by default and should not be removable
			configKey: "StartContent_CanIfInTreeFlow",
			addMutation: () => {},
			addMutationPayload: {},
			editable: false,
		},
		{
			configKey: "StartContent_CanIfUnlocked",
			addMutation: (input: AddCanIfUnlockedStartContentConfigInput) => {
				addCanIfUnlockedStartContentConfig({
					variables: {
						input,
					},
					updater: setDraft,
				});
			},
			addMutationPayload: {},
			editable: false,
		},
		{
			configKey: "StartContent_CanAfterStartingTree",
			addMutation: (input: AddCanAfterStartingTreeStartContentConfigInput) => {
				addCanAfterStartingTreeConfig({
					variables: {
						input,
					},
					updater: setDraft,
				});
			},
			addMutationPayload: {},
			editable: false,
		},
		{
			configKey: "StartContent_CanNDaysAfterStartingTree",
			addMutation: (input: AddCanAfterNDaysStartingTreeStartContentConfigInput) => {
				setPendingNDaysInput(input);
				setIsNDaysVisible(true);
			},
			editable: false,
		},
		{
			configKey: "StartContent_CanIfCertificationUnlocked",
			addMutation: (input: AddCanIfCertificationUnlockedStartContentConfigInput) => {
				addCanIfCertificationUnlockedStartContentConfig({
					variables: {
						input,
					},
					updater: setDraft,
				});
			},
			addMutationPayload: {},
			editable: false,
		},
	];
	const options: Array<ConfigDropdownOptions<StartContentConfigType>> = configs.map((c) => {
		return {
			label: c.configKey,
			value: c.configKey,
		};
	});

	const dropDownOptions = options.filter((o) => o.value !== "StartContent_CanIfInTreeFlow");

	const handleOnChange = (e: DropdownChangeEvent) => {
		const selectedConfig = configs.find((c) => c.configKey === e.value);

		if (selectedConfig) {
			selectedConfig.addMutation({
				contentId: treeNode.id,
				...selectedConfig.addMutationPayload,
			});
		}
	};

	const handleOnDelete = (
		configuration: ConfigurationGraphqlInterface<StartContentConfigType>,
	) => {
		if (configuration.configType === "StartContent_CanIfInTreeFlow") return;
		removeStartContentConfig({
			variables: {
				input: {
					contentNodeId: treeNode.id,
					configId: configuration.id,
				},
			},
			updater: setDraft,
		});
	};

	const handleNDaysSubmit = () => {
		if (
			numOfDaysInput === null ||
			isNaN(numOfDaysInput) ||
			!Number.isInteger(numOfDaysInput) ||
			numOfDaysInput < 0
		) {
			toast.error(t("errors.invalidPositiveInteger"));
			return;
		}

		if (!pendingNDaysInput) return;

		addCanAfterNDaysStartingTreeConfig({
			variables: {
				input: {
					...pendingNDaysInput,
					days: numOfDaysInput,
				},
			},
			updater: setDraft,
		});
		onNDaysHide();
	};

	const onNDaysHide = () => {
		setIsNDaysVisible(false);
		setNumOfDaysInput(null);
		setPendingNDaysInput(null);
	};
	return (
		<>
			<Card className="mb-2">
				<h2>{t("educationalOffer.startContent")}</h2>

				<ConfigDropdown<StartContentConfigType>
					configOptions={dropDownOptions}
					onChange={handleOnChange}
					isPresentational={false}
				/>

				<ConfigPreview<ConfigurationGraphqlInterface<StartContentConfigType>>
					selectedConfigs={
						treeNode?.typeDefinition
							?.startConfigs as ConfigurationGraphqlInterface<StartContentConfigType>[]
					}
					template={(configuration) => {
						const isNDaysConfig =
							configuration.configType === "StartContent_CanNDaysAfterStartingTree";
						const days = isNDaysConfig
							? (
									configuration as unknown as AddCanAfterNDaysStartingTreeStartContentConfigInput
							  )?.days
							: null;
						return (
							<>
								<ConfigItem
									isPresentational={true}
									isLoading={isRemovingStartContentConfig}
									configType={configuration?.configType}
									canEdit={
										configs.find(
											(c) => c.configKey === configuration.configType,
										)?.editable as boolean
									}
									configOptions={options}
									onDelete={() => {
										handleOnDelete(configuration);
									}}
								/>
								{isNDaysConfig && days !== null && (
									<span>{t("educationalOffer.daysCount", { days })}</span>
								)}
							</>
						);
					}}
				/>
			</Card>
			<Dialog
				visible={isNDaysVisible}
				onHide={onNDaysHide}
				title={t("educationalOffer.numberOfDays")}
			>
				<div className={startContentConfigDialogWrapperClass}>
					<div className={inputWrapperClass}>
						<label htmlFor="days" className={labelClass}>
							{t("educationalOffer.enterDays")}
						</label>
						<InputNumber
							id="days"
							value={numOfDaysInput}
							onValueChange={(e) => setNumOfDaysInput(e.value ?? null)}
							min={0}
							className={inputClass}
						/>
					</div>

					<div className={buttonRowClass}>
						<Button
							label={t("actions.save")}
							onClick={handleNDaysSubmit}
							className={buttonClass}
						/>
						<Button
							label={t("actions.back")}
							onClick={onNDaysHide}
							className={`${buttonClass} p-button-secondary`}
						/>
					</div>
				</div>
			</Dialog>
		</>
	);
};
